'use client';

/**
 * API namespace `academy`.
 * Verbatim extraction from frontend/src/lib/api-client.ts (L5512-L5740).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

// ─── V112-E (SPEC-ACADEMY-UGC-HOTMART §3.1) — UGC field extension ────────────
// The backend Course model was extended additively in V112-A with these
// optional fields (Anti-P-382: additive, NO remodel). The shared type
// `AcademyCourse` in @profileia/shared does NOT include them yet (legacy
// shape from Fase 0). We declare a local extension interface here so the
// V112-E Course Landing page can read `course.priceType`, `course.minTier`,
// `course.priceCredits`, `course.revenueSplit`, `course.instructorId` with
// type safety. All fields optional for backward compat with courses
// ia/admin pre-V112-A (default values applied server-side: priceType='free',
// minTier='free', priceCredits='0', revenueSplit=70, instructorId=null).
//
// §6 Anti-Invención: the field names + types mirror the Mongoose schema
// verbatim (course.model.ts L73-L82). NO invented fields.
export type AcademyUgcPriceType = 'free' | 'paid';
export type AcademyUgcMinTier =
  | 'free' | 'basic' | 'bronce' | 'plata' | 'oro' | 'platino' | 'diamante';

export interface AcademyCourseUgc extends AcademyCourse {
  instructorId?: string | null;
  priceType?: AcademyUgcPriceType;
  minTier?: AcademyUgcMinTier;
  priceCredits?: string; // INV-SEC-02: Decimal string
  revenueSplit?: number; // 70 default (70/30 split)
  moderationStatus?: 'auto_published' | 'flagged' | 'removed';
  moderationNotes?: string | null;
}

/**
 * `CourseDetailResponse` — shape returned by `GET /api/academy/courses/:slug`
 * after V112-A extended the Course model with UGC fields. The backend
 * returns the full lean doc (which now includes `priceType`, `minTier`, etc.)
 * under `data.course`, plus the module list + user's enrollment.
 */
export interface CourseDetailResponse {
  course: AcademyCourseUgc;
  modules: AcademyModuleListItem[];
  enrollment: AcademyEnrollmentSummary | null;
}

/**
 * `RedeemResult` — response of `POST /api/academy/courses/:slug/redeem` (V112-B).
 * Mirror verbatim of `RedeemResult` in
 * `backend/src/services/academy-redeem.service.ts` L80-L84.
 */
export interface RedeemResult {
  enrolled: true;
  courseId: string;
  courseSlug: string;
}

export const academy = {
  /**
   * GET /api/academy/courses — list accessible courses for the
   * requesting user (gated by tier per INV-ACAD-02). The response
   * includes the user's `tier` + `entrepreneurshipLimit` so the catalog
   * page can show "Tu paquete (Basic) incluye 20 cursos" + the
   * "Mejora a Oro+" CTA.
   *
   * Spec ref: SPEC-071 §4.1.
   */
  async listCourses(signal?: AbortSignal): Promise<AcademyCatalogResponse> {
    return apiFetch<AcademyCatalogResponse>('/academy/courses', { signal });
  },
  /**
   * GET /api/academy/courses/:slug — course detail + module list +
   * user's enrollment (if any).
   *
   * Spec ref: SPEC-071 §4.2.
   */
  async getCourse(slug: string, signal?: AbortSignal): Promise<{
    course: AcademyCourse;
    modules: AcademyModuleListItem[];
    enrollment: AcademyEnrollmentSummary | null;
  }> {
    return apiFetch(`/academy/courses/${encodeURIComponent(slug)}`, { signal });
  },
  /**
   * GET /api/academy/courses/:slug/module/:order — module detail with
   * markdown content + resources + completion state.
   *
   * Spec ref: SPEC-071 §4.3.
   */
  async getModule(
    slug: string,
    order: number,
    signal?: AbortSignal,
  ): Promise<{ module: AcademyModuleDetail; totalModules: number; hasNextModule: boolean }> {
    return apiFetch(
      `/academy/courses/${encodeURIComponent(slug)}/module/${encodeURIComponent(String(order))}`,
      { signal },
    );
  },
  /**
   * POST /api/academy/enroll — idempotent enrollment.
   *
   * Spec ref: SPEC-071 §4.4. Idempotency: re-enrolling in the same
   * course returns the existing enrollment (no duplicate insert thanks
   * to the Enrollment `{userId, courseId}` unique compound index —
   * INV-ACAD-06 indirectly).
   */
  async enroll(courseId: string): Promise<{ enrollment: AcademyEnrollmentSummary & { _id: string } }> {
    return apiFetch('/academy/enroll', {
      method: 'POST',
      body: JSON.stringify({ courseId }),
    });
  },
  /**
   * POST /api/academy/progress — mark module complete (idempotente
   * INV-ACAD-06). Returns the new progress percentage + whether the
   * course was completed (and the issued certificate if so).
   *
   * Spec ref: SPEC-071 §4.5.
   */
  async markModuleComplete(
    enrollmentId: string,
    moduleId: string,
    timeSpentSeconds: number = 0,
  ): Promise<AcademyMarkProgressResponse> {
    return apiFetch<AcademyMarkProgressResponse>('/academy/progress', {
      method: 'POST',
      body: JSON.stringify({ enrollmentId, moduleId, timeSpentSeconds }),
    });
  },
  /**
   * GET /api/academy/my-courses?status=in_progress|completed|all —
   * user's enrollments with course populated.
   *
   * Spec ref: SPEC-071 §4.7.
   */
  async listMyCourses(
    status?: 'in_progress' | 'completed' | 'all',
    signal?: AbortSignal,
  ): Promise<{ enrollments: AcademyMyCourse[] }> {
    const query = new URLSearchParams();
    if (status) query.set('status', status);
    const qs = query.toString();
    return apiFetch(`/academy/my-courses${qs ? `?${qs}` : ''}`, { signal });
  },
  /**
   * GET /api/academy/my-certificates — user's certificates with course
   * populated (for /app/academy/my-certificates).
   *
   * Spec ref: SPEC-071 §4.8.
   */
  async listMyCertificates(signal?: AbortSignal): Promise<{ certificates: AcademyMyCertificate[] }> {
    return apiFetch('/academy/my-certificates', { signal });
  },
  /**
   * GET /api/academy/certificate/:number — PUBLIC verification
   * (no auth required). Returns `{ valid: false }` if the cert is not
   * found / voided / orphaned. Returns the cert + course + user fields
   * otherwise.
   *
   * Spec ref: SPEC-071 §4.9. Used by /verify/cert/[number] frontend.
   */
  async verifyCertificate(
    number: string,
    signal?: AbortSignal,
  ): Promise<AcademyVerifyResult> {
    return apiFetch<AcademyVerifyResult>(
      `/academy/certificate/${encodeURIComponent(number)}`,
      { auth: false, skipRefresh: true, signal },
    );
  },
  /**
   * GET /api/academy/users/:username/certificates — PUBLIC (no auth).
   * Returns the user's active certifications for the vCard pública
   * (`/p/{username}`). INV-ACAD-05: sin tope visible.
   *
   * Spec ref: SPEC-071 §5.7, §4 (added via the academy controller).
   */
  async listPublicUserCertificates(
    username: string,
    signal?: AbortSignal,
  ): Promise<{ certifications: IUserCertification[] }> {
    return apiFetch(
      `/academy/users/${encodeURIComponent(username)}/certificates`,
      { auth: false, skipRefresh: true, signal },
    );
  },
  // ═══════════════════════════════════════════════════════════════════════
  //  SPEC-077 Fase 2 — Test de Perfil Emprendedor + Matriz de Descubrimiento
  //
  //  3 typed wrappers around the backend endpoints added in PR #132:
  //    - GET  /api/academy/profile-test — get current user's entrepreneur
  //      profile (returns { profile: null } if the user hasn't taken the
  //      Test de Perfil yet — NOT 404, so the wizard can pre-fill on
  //      re-take + the academy catalog page can show the right CTA).
  //    - POST /api/academy/profile-test — upsert the profile (unique index
  //      on userId → idempotent re-take; `takenAt` is preserved on update
  //      via $setOnInsert in `entrepreneur.service.ts:saveProfile`).
  //    - GET  /api/academy/discovery — top 10 ideas based on the profile
  //      (returns 400 with `code: 'ENTREPRENEUR_PROFILE_REQUIRED'` if the
  //      user hasn't taken the test → the discovery page redirects to
  //      /app/academy/profile-test).
  //
  //  §6 Anti-Invención: the request + response shapes are verbatim mirrors
  //  of `backend/src/controllers/academy.controller.ts` lines 642-705 +
  //  `backend/src/services/entrepreneur.service.ts`. NO invented shapes.
  //  NO invented gating — Test + Discovery are FREE for all authed users
  //  (SPEC-077 §1.3, exception to INV-ACAD-02).
  //
  //  CM-28 (AbortController): `getProfileTest` + `getDiscovery` accept an
  //  optional `signal` so callers in React useEffect can cancel the
  //  in-flight request on unmount.
  //  CM-19 (single source of truth): types come from @profileia/shared.
  // ═══════════════════════════════════════════════════════════════════════
  /**
   * GET /api/academy/profile-test — get the current user's entrepreneur
   * profile. Returns `{ profile: null }` if the user hasn't taken the
   * Test de Perfil yet (NOT 404 — the wizard + academy catalog page use
   * this to distinguish "no profile" from "server error").
   *
   * Spec ref: SPEC-077 §1.1.
   */
  async getProfileTest(
    signal?: AbortSignal,
  ): Promise<{ profile: EntrepreneurProfile | null }> {
    return apiFetch<{ profile: EntrepreneurProfile | null }>(
      '/academy/profile-test',
      { signal },
    );
  },
  /**
   * POST /api/academy/profile-test — save/update the entrepreneur profile.
   * Idempotent (unique index on userId → re-take = upsert, `takenAt`
   * preserved via $setOnInsert).
   *
   * Spec ref: SPEC-077 §1.1.
   */
  async saveProfileTest(
    input: EntrepreneurProfileInput,
  ): Promise<{ profile: EntrepreneurProfile }> {
    return apiFetch<{ profile: EntrepreneurProfile }>(
      '/academy/profile-test',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    );
  },
  /**
   * GET /api/academy/discovery — top 10 ideas based on the user's
   * entrepreneur profile. Throws `ApiClientError(code='ENTREPRENEUR_PROFILE_REQUIRED', status=400)`
   * if the user hasn't taken the Test de Perfil yet — the discovery
   * page catches this + redirects to /app/academy/profile-test.
   *
   * Spec ref: SPEC-077 §1.2.
   */
  async getDiscovery(signal?: AbortSignal): Promise<DiscoveryResponse> {
    return apiFetch<DiscoveryResponse>('/academy/discovery', { signal });
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  V112-E (SPEC-ACADEMY-UGC-HOTMART §3.4 + §3.5 + §5.1) — Course Landing
  //
  //  3 typed wrappers for the V112-E Course Landing Hotmart-style UX:
  //    - GET  /academy/courses/:slug        → course detail + modules +
  //      enrollment (typed as `CourseDetailResponse` with the V112-A UGC
  //      fields exposed). This is a typed alias of the existing `getCourse`
  //      method — the response shape is the SAME, but the return type is
  //      widened to `CourseDetailResponse` so the Landing page can read
  //      `course.priceType`, `course.minTier`, etc. without `as any` casts
  //      (Anti-P-041).
  //    - POST /academy/courses/:slug/redeem → canjear un CouponPurchase
  //      redemptionCode para acceso al curso (V112-B). Body
  //      `{ redemptionCode: 'CUP-XXXXXXXX' }`, returns `{ enrolled, courseId,
  //      courseSlug }`. Errors are surfaced as `ApiClientError(code=...)`
  //      with specific codes per the service (COUPON_NOT_FOUND,
  //      COUPON_ALREADY_REDEEMED, COUPON_EXPIRED, COUPON_COURSE_MISMATCH,
  //      etc.).
  //    - enrollCourse(slug) → 2-step helper that fetches the course detail
  //      by slug to resolve the `_id`, then calls the existing `enroll`
  //      endpoint (POST /academy/enroll with body `{ courseId }`). The
  //      existing `enroll(courseId)` method is preserved (RT-1: no change
  //      to existing public API). The new `enrollCourse(slug)` is a
  //      convenience wrapper for the V112-E Pricing Card flow where the
  //      page only has the slug (URL param) — it avoids the caller needing
  //      to do the 2-step manually.
  //  §6 Anti-Invención: the request + response shapes are verbatim mirrors
  //  of `backend/src/features/academy/controllers/redeem.ts` +
  //  `courses.ts:GET /courses/:slug` + `enrollment.ts:POST /enroll`.
  //  CM-28 (AbortController): getCourseBySlug accepts an optional `signal`
  //  so callers in React useEffect can cancel the in-flight request.
  // ═══════════════════════════════════════════════════════════════════════
  /**
   * GET /api/academy/courses/:slug — typed alias returning
   * `CourseDetailResponse` (with V112-A UGC fields exposed on
   * `course`). Same wire shape as `getCourse()` above — the difference
   * is purely the return type annotation (the runtime payload is
   * identical, the backend returns the full lean doc which already
   * includes `priceType`, `minTier`, etc.).
   *
   * Spec ref: SPEC-ACADEMY-UGC-HOTMART §3.5 (Course Landing page source).
   */
  async getCourseBySlug(slug: string, signal?: AbortSignal): Promise<CourseDetailResponse> {
    return apiFetch<CourseDetailResponse>(
      `/academy/courses/${encodeURIComponent(slug)}`,
      { signal },
    );
  },
  /**
   * POST /api/academy/courses/:slug/redeem — canjear un código de acceso
   * `CUP-XXXXXXXX` para inscribirse en un curso priceType='paid'.
   *
   * Body: `{ redemptionCode: string }` (format `CUP-XXXXXXXX` — validated
   * by the backend Zod schema `redeemSchema` in
   * `backend/src/features/academy/controllers/redeem.ts`).
   *
   * Response: `{ enrolled: true, courseId, courseSlug }` on success.
   *
   * Errors surface as `ApiClientError` with `code` field set to one of:
   *   - VALIDATION_ERROR (400): código no matchea el formato `CUP-XXXXXXXX`.
   *   - COURSE_NOT_FOUND (404): slug no existe o no es `status='published'`.
   *   - VALIDATION_ERROR (400): curso es priceType='free' (no requiere canje).
   *   - COUPON_NOT_FOUND (404): redemptionCode no existe en CouponPurchase.
   *   - COUPON_ALREADY_REDEEMED (409): purchase.status='redeemed' (race-safe).
   *   - COUPON_EXPIRED (410): purchase.status='expired' OR expiresAt < now.
   *   - COUPON_REFUNDED (410): purchase.status='refunded'.
   *   - COUPON_NOT_COURSE_ACCESS (400): Coupon no tiene `courseAccess` set.
   *   - COUPON_COURSE_MISMATCH (403): Coupon.courseAccess.courseId ≠ course._id.
   *   - AUTH_REQUIRED (401): no auth / token expired (refresh fails).
   *
   * Spec ref: SPEC-ACADEMY-UGC-HOTMART §3.4 (endpoint de canje).
   */
  async redeemCourseAccess(
    slug: string,
    redemptionCode: string,
  ): Promise<RedeemResult> {
    return apiFetch<RedeemResult>(
      `/academy/courses/${encodeURIComponent(slug)}/redeem`,
      {
        method: 'POST',
        body: JSON.stringify({ redemptionCode }),
      },
    );
  },
  /**
   * enrollCourse(slug) — 2-step helper for the V112-E Pricing Card flow:
   *   1. GET /academy/courses/:slug → resolve `_id` (the existing
   *      POST /academy/enroll endpoint takes `courseId`, not slug).
   *   2. POST /academy/enroll body `{ courseId }` → idempotent enrollment.
   *
   * Idempotency: the existing `enroll(courseId)` is idempotent (unique
   * compound index `{userId, courseId}` on Enrollment collection — INV-ACAD-06
   * indirectly). So calling `enrollCourse(slug)` twice for the same user +
   * course returns the existing enrollment on the second call (no duplicate).
   *
   * Spec ref: SPEC-ACADEMY-UGC-HOTMART §5.1 (Acceso a cursos — priceType='free'
   *           + tier>=minTier → enrollUserInCourse).
   */
  async enrollCourse(slug: string): Promise<{ enrollment: AcademyEnrollmentSummary & { _id: string } }> {
    const detail = await this.getCourseBySlug(slug);
    return this.enroll(detail.course._id);
  },
};
